/**
 * Input Aggregator
 *
 * Builds a MonthlyInputReport from normalized input records by:
 * 1. Grouping records by dateKey
 * 2. Organizing each day's records into meal categories (breakfast, lunch, teaTime, dinner, fruits, snack)
 * 3. Computing feeding type summary (Normal, NGT, Peg Tube counts)
 * 4. Computing note summary (total notes, refused count, partial intake count)
 * 5. Preserving note-only sessions (empty food + non-empty notes) in the meal matrix
 */

import {
  NormalizedInputRecord,
  MonthlyInputReport,
  DailyInputRow,
  MealSession,
  SeverityTag,
} from '../types';

/** Pattern for refused/declined keywords in notes */
const REFUSED_PATTERN = /refused|declined|reject/i;

/** Pattern for partial intake keywords in notes */
const PARTIAL_INTAKE_PATTERN = /half|few|partial/i;

/** Pattern for clinical attention keywords in notes */
const CLINICAL_ATTENTION_PATTERN = /pain|wound|bleeding|fever|fall|dizzy|faint|doctor/i;

/**
 * Classify note text into severity tags for a MealSession.
 * Uses keyword matching similar to noteTagger but operates on plain text strings.
 * Returns an array of applicable tags (priority order: Clinical Attention > Refused > Partial Intake > Information).
 */
function classifyInputNoteSeverity(notes: string): SeverityTag[] {
  if (!notes || notes.trim() === '') return [];

  const text = notes.toLowerCase();

  if (CLINICAL_ATTENTION_PATTERN.test(text)) return ['Clinical Attention'];
  if (REFUSED_PATTERN.test(text)) return ['Refused'];
  if (PARTIAL_INTAKE_PATTERN.test(text)) return ['Partial Intake'];
  return ['Information'];
}

/**
 * Map a meal code (1-6) to the corresponding meal key in DailyInputRow.meals
 */
function getMealKey(mealCode: number): keyof DailyInputRow['meals'] | null {
  switch (mealCode) {
    case 1: return 'breakfast';
    case 2: return 'lunch';
    case 3: return 'teaTime';
    case 4: return 'dinner';
    case 5: return 'fruits';
    case 6: return 'snack';
    default: return null;
  }
}

/**
 * Create an empty meals object for a DailyInputRow.
 */
function createEmptyMeals(): DailyInputRow['meals'] {
  return {
    breakfast: [],
    lunch: [],
    teaTime: [],
    dinner: [],
    fruits: [],
    snack: [],
  };
}

/**
 * Convert a NormalizedInputRecord into a MealSession.
 */
function toMealSession(record: NormalizedInputRecord): MealSession {
  return {
    inputId: record.id,
    timeLabel: record.timeLabel,
    feedingTypeCode: record.feedingTypeCode,
    feedingTypeLabel: record.feedingTypeLabel,
    foodItems: record.foodItems,
    notes: record.notes,
    noteTags: classifyInputNoteSeverity(record.notes),
    creatorName: record.creatorName,
  };
}

/**
 * Aggregate normalized input records into a MonthlyInputReport.
 *
 * @param records - Array of normalized input records for the month
 * @param startDate - Start date of the report period (YYYY-MM-DD)
 * @param endDate - End date of the report period (YYYY-MM-DD)
 * @returns A MonthlyInputReport with daily rows, feeding type summary, and note summary
 */
export function aggregateInput(
  records: NormalizedInputRecord[],
  startDate: string,
  endDate: string
): MonthlyInputReport {
  const patientId = records.length > 0 ? records[0].patientId : '';

  // Group records by dateKey
  const dateGroups = new Map<string, NormalizedInputRecord[]>();
  for (const record of records) {
    const existing = dateGroups.get(record.dateKey);
    if (existing) {
      existing.push(record);
    } else {
      dateGroups.set(record.dateKey, [record]);
    }
  }

  // Build daily rows
  const dailyRows: DailyInputRow[] = [];
  for (const [dateKey, dayRecords] of dateGroups) {
    const meals = createEmptyMeals();

    for (const record of dayRecords) {
      const mealKey = getMealKey(record.mealCode);
      if (mealKey) {
        meals[mealKey].push(toMealSession(record));
      }
    }

    dailyRows.push({ dateKey, meals });
  }

  // Sort daily rows by dateKey ascending
  dailyRows.sort((a, b) => a.dateKey.localeCompare(b.dateKey));

  // Compute feeding type summary
  let normalCount = 0;
  let ngtCount = 0;
  let pegTubeCount = 0;

  for (const record of records) {
    switch (record.feedingTypeCode) {
      case 1:
        normalCount++;
        break;
      case 2:
        ngtCount++;
        break;
      case 3:
        pegTubeCount++;
        break;
    }
  }

  // Compute note summary
  let totalInputNotes = 0;
  let refusedCount = 0;
  let partialIntakeCount = 0;

  for (const record of records) {
    if (record.notes && record.notes.trim() !== '') {
      totalInputNotes++;
      if (REFUSED_PATTERN.test(record.notes)) {
        refusedCount++;
      }
      if (PARTIAL_INTAKE_PATTERN.test(record.notes)) {
        partialIntakeCount++;
      }
    }
  }

  return {
    patientId,
    startDate,
    endDate,
    dailyRows,
    feedingTypeSummary: {
      normalCount,
      ngtCount,
      pegTubeCount,
    },
    noteSummary: {
      totalInputNotes,
      refusedCount,
      partialIntakeCount,
    },
  };
}
